/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 5;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   * @param {string} [productHandle] - The handle of the product to add.
   */
  static addProduct(productId, productHandle) {
    let viewedProducts = this.#getEntries();

    viewedProducts = viewedProducts.filter((entry) => entry.id !== productId);
    viewedProducts.unshift({ id: productId, handle: productHandle || null });
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Updates the handle for a stored product entry.
   * @param {string} productId
   * @param {string} productHandle
   */
  static updateHandle(productId, productHandle) {
    const viewedProducts = this.#getEntries().map((entry) => {
      if (entry.id === productId) {
        return { id: entry.id, handle: productHandle };
      }

      return entry;
    });

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  /**
   * Retrieves the list of recently viewed product IDs.
   * @returns {string[]} The list of viewed product IDs.
   */
  static getProducts() {
    return this.#getEntries().map((entry) => entry.id);
  }

  /**
   * Retrieves recently viewed products with handles for section rendering.
   * @returns {{ id: string, handle: string | null }[]}
   */
  static getEntries() {
    return this.#getEntries();
  }

  /**
   * @returns {{ id: string, handle: string | null }[]}
   */
  static #getEntries() {
    const stored = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');

    return stored.map((/** @type {string | { id: string, handle?: string | null }} */ item) => {
      if (typeof item === 'string') {
        return { id: item, handle: null };
      }

      return { id: item.id, handle: item.handle ?? null };
    });
  }
}
